import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTemplateEditorHistory } from "../editor/useTemplateEditorHistory";
import {
  applyOverlayBox,
  deleteNodeById,
  findNodePath,
  insertNodeAtOverlay,
  listNodeNames,
  patchNodeById,
  resolveInsertParentID,
} from "../utils/layerTree";
import {
  addInstanceAsset,
  applySiblingDisplayOrder,
  cloneInstance,
  createInstanceNode,
  flattenInstanceItems,
} from "./instanceGraph";

const hydrate = (instance) => ({
  artboardID: instance?.artboardID || "primary",
  width: Math.round(Number(instance?.width) || 0),
  height: Math.round(Number(instance?.height) || 0),
  nodes: instance?.nodes || {},
  nodeLayouts: instance?.nodeLayouts || {},
  assets: instance?.assets || {},
});

const useTemplateInstance = ({ instance, enabled }) => {
  const [artboardID, setArtboardID] = useState("primary");
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const [nodes, setNodes] = useState({});
  const [nodeLayouts, setNodeLayouts] = useState({});
  const [assets, setAssets] = useState({});
  const [selectedID, setSelectedID] = useState(null);
  const [editingTextID, setEditingTextID] = useState(null);
  const [expandedIDs, setExpandedIDs] = useState({});
  const [layersDirty, setLayersDirty] = useState(false);
  const selectedIDRef = useRef(null);
  const graphRef = useRef({
    nodes: {},
    nodeLayouts: {},
    assets: {},
  });

  selectedIDRef.current = selectedID;
  graphRef.current = { nodes, nodeLayouts, assets };

  const getSnapshot = useCallback(
    () => ({
      artboardID,
      width,
      height,
      nodes,
      nodeLayouts,
      assets,
      selectedID,
      expandedIDs,
    }),
    [
      artboardID,
      assets,
      expandedIDs,
      height,
      nodeLayouts,
      nodes,
      selectedID,
      width,
    ]
  );

  const applySnapshot = useCallback((snapshot) => {
    setArtboardID(snapshot.artboardID);
    setWidth(snapshot.width);
    setHeight(snapshot.height);
    setNodes(snapshot.nodes || {});
    setNodeLayouts(snapshot.nodeLayouts || {});
    setAssets(snapshot.assets || {});
    setSelectedID(snapshot.selectedID || null);
    setExpandedIDs(snapshot.expandedIDs || {});
    setEditingTextID(null);
    setLayersDirty(true);
  }, []);

  const { push, endCoalesce, undo, redo, clear } = useTemplateEditorHistory({
    getSnapshot,
    applySnapshot,
  });

  useEffect(() => {
    if (!enabled) return;
    const next = hydrate(cloneInstance(instance));
    setArtboardID(next.artboardID);
    setWidth(next.width);
    setHeight(next.height);
    setNodes(next.nodes);
    setNodeLayouts(next.nodeLayouts);
    setAssets(next.assets);
    setSelectedID(null);
    setEditingTextID(null);
    setExpandedIDs({});
    setLayersDirty(false);
    clear();
  }, [clear, enabled, instance]);

  const commitGraph = useCallback(
    ({
      nextNodes = nodes,
      nextLayouts = nodeLayouts,
      nextAssets = assets,
    }) => {
      setNodes(nextNodes);
      setNodeLayouts(nextLayouts);
      setAssets(nextAssets);
      setLayersDirty(true);
    },
    [assets, nodeLayouts, nodes]
  );

  const items = useMemo(
    () =>
      flattenInstanceItems({
        nodes,
        nodeLayouts,
        assets,
      }),
    [assets, nodeLayouts, nodes]
  );

  const selectedItem = items.find((item) => item.id === selectedID) || null;

  const updateBox = useCallback(
    ({ id, nx, ny, nw, nh }) => {
      const found = findNodePath({ nodes: graphRef.current.nodes, nodeID: id });
      if (found?.node?.isBackground && !found.parentID) {
        return;
      }
      push({ coalesceKey: `transform:${id}` });
      const result = applyOverlayBox({
        nodes: graphRef.current.nodes,
        nodeLayouts: graphRef.current.nodeLayouts,
        nodeID: id,
        nx,
        ny,
        nw,
        nh,
        artboardWidth: width,
        artboardHeight: height,
      });
      commitGraph({
        nextNodes: result.nodes,
        nextLayouts: result.nodeLayouts,
      });
    },
    [commitGraph, height, push, width]
  );

  const patchSelected = useCallback(
    ({ patch, assetPatch }) => {
      if (!selectedIDRef.current) return;
      push();
      const nextNodes = patch
        ? patchNodeById({
            nodes: graphRef.current.nodes,
            nodeID: selectedIDRef.current,
            patch,
          })
        : graphRef.current.nodes;
      commitGraph({
        nextNodes,
        nextAssets: assetPatch
          ? { ...graphRef.current.assets, ...assetPatch }
          : graphRef.current.assets,
      });
    },
    [commitGraph, push]
  );

  const renameLayer = useCallback(
    (nodeID, name) => {
      const trimmed = String(name || "").trim();
      if (!trimmed) return;
      push();
      commitGraph({
        nextNodes: patchNodeById({
          nodes: graphRef.current.nodes,
          nodeID,
          patch: { name: trimmed },
        }),
      });
    },
    [commitGraph, push]
  );

  const setTextValue = useCallback(
    (nodeID, value) => {
      const found = findNodePath({
        nodes: graphRef.current.nodes,
        nodeID,
      });
      if (found?.node?.type !== "text") return;
      push({ coalesceKey: `text:${nodeID}` });
      commitGraph({
        nextNodes: patchNodeById({
          nodes: graphRef.current.nodes,
          nodeID,
          patch: {
            source: {
              kind: "fixed",
              value: String(value || ""),
            },
          },
        }),
      });
    },
    [commitGraph, push]
  );

  const addLayer = useCallback(
    ({ type, url, name, id }) => {
      if (!id) return null;
      const parentID = resolveInsertParentID({
        nodes: graphRef.current.nodes,
        selectedID: selectedIDRef.current,
      });
      let nextAssets = graphRef.current.assets;
      const node = createInstanceNode({
        id,
        name,
        existingNames: listNodeNames({ nodes: graphRef.current.nodes }),
        type,
      });
      if (type === "image" && url) {
        const added = addInstanceAsset({
          assets: nextAssets,
          name: node.name,
          url,
        });
        nextAssets = added.assets;
        node.source = { kind: "asset", assetRef: added.assetRef };
      }
      const result = insertNodeAtOverlay({
        nodes: graphRef.current.nodes,
        nodeLayouts: graphRef.current.nodeLayouts,
        parentID,
        node,
        nx: 0.12,
        ny: 0.12,
        nw: type === "text" ? 0.4 : 0.28,
        nh: type === "text" ? 0.12 : 0.28,
        artboardWidth: width,
        artboardHeight: height,
      });
      if (!result.ok) return null;
      push();
      commitGraph({
        nextNodes: result.nodes,
        nextLayouts: result.nodeLayouts,
        nextAssets,
      });
      setSelectedID(id);
      if (parentID) {
        setExpandedIDs((current) => ({ ...current, [parentID]: true }));
      }
      return id;
    },
    [commitGraph, height, push, width]
  );

  const deleteLayer = useCallback(
    (nodeID) => {
      const id = nodeID || selectedIDRef.current;
      if (!id) return;
      push();
      const result = deleteNodeById({
        nodes: graphRef.current.nodes,
        nodeLayouts: graphRef.current.nodeLayouts,
        nodeID: id,
      });
      commitGraph({
        nextNodes: result.nodes,
        nextLayouts: result.nodeLayouts,
      });
      if (
        !findNodePath({
          nodes: result.nodes,
          nodeID: selectedIDRef.current,
        })
      ) {
        setSelectedID(null);
      }
      setEditingTextID((current) =>
        current && !findNodePath({ nodes: result.nodes, nodeID: current })
          ? null
          : current
      );
    },
    [commitGraph, push]
  );

  const reorderSiblings = useCallback(
    ({ parentID, orderedDisplayIds }) => {
      push();
      const result = applySiblingDisplayOrder({
        nodes: graphRef.current.nodes,
        nodeLayouts: graphRef.current.nodeLayouts,
        parentID,
        orderedDisplayIds,
      });
      commitGraph({
        nextNodes: result.nodes,
        nextLayouts: result.nodeLayouts,
      });
    },
    [commitGraph, push]
  );

  const replaceImage = useCallback(
    ({ nodeID, url, name }) => {
      if (!url) return;
      push();
      const added = addInstanceAsset({
        assets: graphRef.current.assets,
        name: name || nodeID,
        url,
      });
      commitGraph({
        nextNodes: patchNodeById({
          nodes: graphRef.current.nodes,
          nodeID,
          patch: {
            source: { kind: "asset", assetRef: added.assetRef },
          },
        }),
        nextAssets: added.assets,
      });
    },
    [commitGraph, push]
  );

  const getPersistInstance = useCallback(
    () => ({
      artboardID,
      width,
      height,
      nodes,
      nodeLayouts,
      assets,
    }),
    [artboardID, assets, height, nodeLayouts, nodes, width]
  );

  return {
    artboardID,
    width,
    height,
    nodes,
    nodeLayouts,
    assets,
    items,
    selectedID,
    selectedItem,
    editingTextID,
    expandedIDs,
    layersDirty,
    setSelectedID,
    setEditingTextID,
    setExpandedIDs,
    updateBox,
    patchSelected,
    renameLayer,
    setTextValue,
    addLayer,
    deleteLayer,
    reorderSiblings,
    replaceImage,
    undo,
    redo,
    endCoalesce,
    getPersistInstance,
  };
};

export default useTemplateInstance;
